import { Annotation, Column, Group } from '@studio/design-system/form';
import { Mediator } from '@studio/design-system/input';
import { clamp } from '@studio/js-utils/range';
import { ControlDefaults } from '@studio/models/control';
import { Limits, ViscaSerialDefaults } from '@studio/models/control/visca-serial';
import type { BaudRate as M_BaudRate } from '@studio/models/net';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn } from '@studio/react-utils/hooks';
import { toNumber } from '@tryiris-ai/js-types';
import { defaults } from 'lodash-es';
import type { ReactElement, SyntheticEvent } from 'react';
import { Slugs } from './constants.js';
import type { ViscaSerialProps } from './types.js';
import { Advanced } from '#~/common/editor/index.js';
import { BaudRate } from '#~/device/components/baud-rate/index.js';
import { Parity } from '#~/device/components/parity/index.js';

/** Visca/Serial settings. */
export const ViscaSerial = ({ settings, ...props }: ViscaSerialProps): ReactElement => {
  const current = defaults({}, settings.value, ViscaSerialDefaults, ControlDefaults);

  const handleAddressChange = useLatestFn((value: string, event: SyntheticEvent) => {
    const address = clamp(toNumber(value, { default: Limits.minAddress, positive: true }), {
      max: Limits.maxAddress,
      min: Limits.minAddress
    });

    if (address && address !== current.address) {
      settings({ ...current, address });
    } else {
      event.preventDefault();
    }
  });

  const handleParityChange = useLatestFn((parity: boolean) => {
    if (parity !== current.parity) {
      settings({ ...current, parity });
    }
  });

  const handlePortChange = useLatestFn((value: string, event: SyntheticEvent) => {
    const port = clamp(toNumber(value, { default: Limits.minPort, positive: true }), {
      max: Limits.maxPort,
      min: Limits.minPort
    });

    if (port && port !== current.port) {
      settings({ ...current, port });
    } else {
      event.preventDefault();
    }
  });

  const handleBaudRateChange = useLatestFn((baudRate: M_BaudRate) => {
    if (baudRate !== current.baudRate) {
      settings({ ...current, baudRate });
    }
  });

  return (
    <Column {...props}>
      <Group annotation={<Annotation.Required />} label='COM port'>
        <Mediator
          max={Limits.maxPort}
          min={Limits.minPort}
          onCommit={handlePortChange}
          pattern='[0-9]*'
          step={1}
          type='number'
          value={current.port}
        />
      </Group>

      <hr className='mt-8' />

      <Advanced slug={Slugs.advanced}>
        <Column>
          <Group label='Address/ID'>
            <Mediator
              max={Limits.maxAddress}
              min={Limits.minAddress}
              onCommit={handleAddressChange}
              pattern='[0-9]*'
              step={1}
              type='number'
              value={current.address}
            />
          </Group>

          <Group label='Baud rate'>
            <BaudRate baudRate={toAccessor(current.baudRate, handleBaudRateChange)} />
          </Group>

          <Group label='Parity'>
            <Parity parity={toAccessor(current.parity, handleParityChange)} />
          </Group>
        </Column>
      </Advanced>
    </Column>
  );
};
